import React from 'react';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';

export default function PrivacyPage() {
  const { language } = useLanguage();

  const content = language === 'fr' ? {
    title: 'Politique de Confidentialité',
    lastUpdated: 'Dernière mise à jour : Juin 2025',
    sections: [
      {
        title: '1. Responsable du traitement',
        text: `OFA France (ci-après "nous" ou "OFA France") est responsable du traitement des données personnelles collectées sur le site www.ofalycee.fr.\n\nContact : info@ofalycee.fr`,
      },
      {
        title: '2. Données collectées',
        text: `Nous collectons les données suivantes lorsque vous utilisez notre site :\n\n• **Formulaire de contact** : prénom, nom, email, téléphone (optionnel), niveau scolaire de l'enfant, message.\n• **Formulaire de partenariat** : nom de l'établissement, nom du contact, email, téléphone, message.\n• **Données de navigation** : adresse IP, type de navigateur, pages visitées, durée de visite (via Google Analytics).\n• **Cookies** : cookies techniques nécessaires au fonctionnement du site et cookies analytiques (avec votre consentement).`,
      },
      {
        title: '3. Finalités du traitement',
        text: `Vos données sont utilisées pour :\n\n• Répondre à vos demandes d'information et de contact.\n• Traiter les demandes de partenariat.\n• Améliorer notre site et nos services grâce à l'analyse de la navigation.\n• Vous informer sur nos programmes et événements (uniquement si vous y avez consenti).`,
      },
      {
        title: '4. Base légale du traitement',
        text: `Le traitement de vos données repose sur :\n\n• **Votre consentement** : pour les cookies analytiques et les communications marketing.\n• **L'exécution d'un contrat ou de mesures précontractuelles** : pour le traitement de votre demande d'admission.\n• **Notre intérêt légitime** : pour améliorer nos services et assurer la sécurité du site.`,
      },
      {
        title: '5. Durée de conservation',
        text: `• **Données des formulaires** : non stockées sur nos serveurs. Les informations sont transmises par email uniquement.\n• **Données de navigation** : conservées pendant 13 mois (Google Analytics).\n• **Cookies** : durée maximale de 13 mois.`,
      },
      {
        title: '6. Partage des données',
        text: `Vos données peuvent être partagées avec :\n\n• **Resend** : pour l'envoi d'emails (traitement des formulaires de contact). Les données transitent uniquement et ne sont pas stockées de manière permanente.\n• **Google Analytics** : pour l'analyse de la navigation (données anonymisées).\n\nAucune donnée personnelle n'est stockée sur nos serveurs. Nous ne vendons jamais vos données personnelles à des tiers.`,
      },
      {
        title: '7. Vos droits (RGPD)',
        text: `Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :\n\n• **Droit d'accès** : obtenir une copie de vos données personnelles.\n• **Droit de rectification** : corriger des données inexactes.\n• **Droit à l'effacement** : demander la suppression de vos données.\n• **Droit à la portabilité** : recevoir vos données dans un format structuré.\n• **Droit d'opposition** : vous opposer au traitement de vos données.\n• **Droit de retrait du consentement** : retirer votre consentement à tout moment.\n\nPour exercer ces droits, contactez-nous à : **info@ofalycee.fr**\n\nVous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).`,
      },
      {
        title: '8. Cookies',
        text: `Notre site utilise des cookies :\n\n• **Cookies essentiels** : nécessaires au fonctionnement du site (préférences de langue). Ils ne nécessitent pas votre consentement.\n• **Cookies analytiques** (Google Analytics) : nous aident à comprendre comment les visiteurs utilisent le site. Ces cookies ne sont activés qu'avec votre consentement.\n\nVous pouvez modifier vos préférences de cookies à tout moment via le bandeau de consentement.`,
      },
      {
        title: '9. Sécurité des données',
        text: `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données, notamment :\n\n• Chiffrement des communications (HTTPS/TLS).\n• Protection contre les attaques XSS et CSRF.\n• Limitation du taux de requêtes (rate limiting).\n• Accès restreint aux données personnelles.`,
      },
      {
        title: '10. Modifications',
        text: `Nous nous réservons le droit de modifier cette politique de confidentialité. Toute modification sera publiée sur cette page avec la date de mise à jour.`,
      },
    ],
  } : {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: June 2025',
    sections: [
      {
        title: '1. Data Controller',
        text: `OFA France (hereinafter "we" or "OFA France") is responsible for processing personal data collected on the website www.ofalycee.fr.\n\nContact: info@ofalycee.fr`,
      },
      {
        title: '2. Data Collected',
        text: `We collect the following data when you use our site:\n\n• **Contact form**: first name, last name, email, phone (optional), child's grade level, message.\n• **Partnership form**: institution name, contact name, email, phone, message.\n• **Browsing data**: IP address, browser type, pages visited, visit duration (via Google Analytics).\n• **Cookies**: essential cookies for site functionality and analytical cookies (with your consent).`,
      },
      {
        title: '3. Purposes of Processing',
        text: `Your data is used to:\n\n• Respond to your information and contact requests.\n• Process partnership inquiries.\n• Improve our website and services through navigation analysis.\n• Inform you about our programs and events (only with your consent).`,
      },
      {
        title: '4. Legal Basis',
        text: `The processing of your data is based on:\n\n• **Your consent**: for analytical cookies and marketing communications.\n• **Performance of a contract or pre-contractual measures**: for processing your admission request.\n• **Our legitimate interest**: to improve our services and ensure site security.`,
      },
      {
        title: '5. Data Retention',
        text: `• **Form data**: not stored on our servers. Information is transmitted by email only.\n• **Browsing data**: retained for 13 months (Google Analytics).\n• **Cookies**: maximum duration of 13 months.`,
      },
      {
        title: '6. Data Sharing',
        text: `Your data may be shared with:\n\n• **Resend**: for email delivery (contact form processing). Data is transmitted only and not permanently stored.\n• **Google Analytics**: for navigation analysis (anonymized data).\n\nNo personal data is stored on our servers. We never sell your personal data to third parties.`,
      },
      {
        title: '7. Your Rights (GDPR)',
        text: `Under the General Data Protection Regulation (GDPR), you have the following rights:\n\n• **Right of access**: obtain a copy of your personal data.\n• **Right to rectification**: correct inaccurate data.\n• **Right to erasure**: request deletion of your data.\n• **Right to data portability**: receive your data in a structured format.\n• **Right to object**: object to the processing of your data.\n• **Right to withdraw consent**: withdraw your consent at any time.\n\nTo exercise these rights, contact us at: **info@ofalycee.fr**\n\nYou may also file a complaint with the CNIL (www.cnil.fr).`,
      },
      {
        title: '8. Cookies',
        text: `Our site uses cookies:\n\n• **Essential cookies**: necessary for site functionality (language preferences). They do not require your consent.\n• **Analytical cookies** (Google Analytics): help us understand how visitors use the site. These cookies are only activated with your consent.\n\nYou can change your cookie preferences at any time via the consent banner.`,
      },
      {
        title: '9. Data Security',
        text: `We implement appropriate technical and organizational measures to protect your data, including:\n\n• Communication encryption (HTTPS/TLS).\n• Protection against XSS and CSRF attacks.\n• Rate limiting.\n• Restricted access to personal data.`,
      },
      {
        title: '10. Changes',
        text: `We reserve the right to modify this privacy policy. Any changes will be published on this page with the update date.`,
      },
    ],
  };

  const renderText = (text) => {
    return text.split('\n').map((line, i) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <span key={i}>
          <span dangerouslySetInnerHTML={{ __html: formatted }} />
          {i < text.split('\n').length - 1 && <br />}
        </span>
      );
    });
  };

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-white">
      <SEO path="/privacy" />
      <Navigation />

      <section className="pt-32 pb-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-[#2D78F6] hover:text-[#14274B] transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <Home className="w-4 h-4" />
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Link>
            <ChevronRight className="w-3 h-3 text-[#475569]/50" />
            <span className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
            </span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-light text-[#14274B] leading-tight tracking-tight mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {content.title}
          </motion.h1>
          <p className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {content.lastUpdated}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {content.sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.05 * index }}
            >
              <h2
                className="text-xl font-semibold text-[#14274B] mb-4"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {section.title}
              </h2>
              <div
                className="text-[#475569] leading-relaxed text-sm"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {renderText(section.text)}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
